import {useId} from "react";
import {Input} from "@/components/ui/input";
import {Textarea} from "@/components/ui/textarea";
import {Label} from "@/components/ui/label";
import {NativeSelect} from "@/components/ui/native-select";
import {Switch} from "@/components/ui/switch";
import type {Blocker,Part,Effect} from "@/components/ui/box.schema";
import type {Audiences,AnnouncementCategory} from "@/lib/api/announcements";
import type {TabDraft} from "@/components/ui/box-draft";
import {inboxWords as words} from "./inbox-copy";
export interface AnnouncementDraft {audience:string;category:AnnouncementCategory;urgent:boolean;title:string;body:string;email:boolean}
/** All audience and effect claims derive from this one authorized audience read. */
/** `course` fixes the audience to one course: a course's own All notices posts only to it. */
export function useAnnouncementDraft(keep:TabDraft<AnnouncementDraft,unknown>,read:Audiences|undefined,change:(patch:Partial<AnnouncementDraft>)=>void,course?:string){
  const prefix=useId();
  const value=keep.value;
  const frozen=Boolean(keep.pending);
  const chosen=course??value.audience;
  const target=chosen==="academy"?read?.can_post_academy:read?.courses.some(item=>item.id===chosen);
  const count=read?.email_recipient_counts?.[chosen];
  const audience=chosen==="academy"?words.academyWide:read?.courses.find(item=>item.id===chosen)?.title??words.selectedAudience;
  const blockers=():Blocker[]=>[
    ...(!target?[{kind:"missing" as const,part:"announcement-audience",sentence:words.chooseAudience}]:[]),
    ...(!value.title.trim()?[{kind:"missing" as const,part:"announcement-title",sentence:words.addNoticeTitle}]:[]),
    ...(!value.body.trim()?[{kind:"missing" as const,part:"announcement-body",sentence:words.writeNotice}]:[]),
    ...(value.email&&count===undefined?[{kind:"missing" as const,part:"announcement-audience",sentence:words.audienceCountUnread}]:[]),
  ];
  const parts:Part[]=[
    {id:"announcement-audience",label:words.audience,owner:{system:"imam"},fold:"never",draft:"browser",
      state:{class:target?"done":"waiting",word:target?words.audienceChosen:words.noAudience},
      render:()=> <div className="grid gap-4">
        {course?<p className="text-ui"><span className="text-muted-foreground">{words.audience}: </span>{target?audience:words.selectedAudienceUnavailable}</p>
        :<div className="grid gap-2"><Label htmlFor={`${prefix}-audience`}>{words.audience}</Label>
          <NativeSelect id={`${prefix}-audience`} disabled={frozen} value={value.audience} onChange={event=>change({audience:event.target.value})}>
            <option value="">{words.chooseAudienceOption}</option>
            {read?.can_post_academy&&<option value="academy">{words.academyWide}</option>}
            {value.audience&&!target&&<option value={value.audience}>{words.selectedAudienceUnavailable}</option>}
            {read?.courses.map(course=><option key={course.id} value={course.id}>{course.code} · {course.title}</option>)}
          </NativeSelect></div>}
        <div className="grid gap-2"><Label htmlFor={`${prefix}-category`}>{words.category}</Label>
          <NativeSelect id={`${prefix}-category`} disabled={frozen} value={value.category} onChange={event=>change({category:event.target.value as AnnouncementCategory})}>
            {Object.entries(words.categories).map(([key,label])=><option key={key} value={key}>{label}</option>)}
          </NativeSelect></div>
        <div className="flex items-center gap-3"><Switch id={`${prefix}-urgent`} disabled={frozen} checked={value.urgent} onCheckedChange={urgent=>change({urgent})}/><Label htmlFor={`${prefix}-urgent`}>{words.markUrgent}</Label></div>
      </div>},
    {id:"announcement-title",label:words.title,owner:{system:"imam"},fold:"never",draft:"browser",
      state:{class:value.title?"draft":"waiting",word:value.title?words.titleWritten:words.noTitle},
      render:()=> <div className="grid gap-2"><Label htmlFor={`${prefix}-title`}>{words.title}</Label>
        <Input id={`${prefix}-title`} disabled={frozen} maxLength={200} value={value.title} onChange={event=>change({title:event.target.value})}/></div>},
    {id:"announcement-body",label:words.message,owner:{system:"imam"},fold:"never",draft:"browser",keep,
      state:{class:value.body?"draft":"waiting",word:value.body?words.messageWritten:words.notWritten},
      render:()=> <div className="grid gap-2"><Label htmlFor={`${prefix}-body`}>{words.message}</Label>
        <Textarea id={`${prefix}-body`} disabled={frozen} value={value.body} onChange={event=>change({body:event.target.value})}/></div>},
  ];
  const effects=():Effect[]=>[
    {id:"post",sentence:words.postTo(audience)},
    {id:"email",sentence:count===undefined?words.emailCountUnread:words.emailAudience(count,audience),optional:{checked:value.email,toggle:()=>{if(!frozen)change({email:!value.email});}}},
  ];
  return {parts,effects,blockers};
}
